import React, { useState } from "react";
import { Box, SpaceBetween, Icon } from "@amzn/awsui-components-react";

/**
 * StarRating - A star-based rating component using Cloudscape Icons.
 *
 * Props:
 *  - value (number): User's personal rating (0-5)
 *  - averageRating (number): Average community rating to display visually (supports half values)
 *  - onChange (function): Callback when a star is clicked, receives the new rating (whole numbers)
 *  - maxStars (number): Total number of stars to display (default: 5)
 *  - readOnly (boolean): If true, disables interaction (default: false)
 *
 * Behavior:
 *  - Stars visually reflect the averageRating by default
 *  - On hover, stars temporarily show the user's prospective selection
 *  - After clicking, stars return to showing the averageRating
 *  - "Your rating: X stars" text shows the user's personal rating below
 */
function StarRating({ value = 0, averageRating = 0, ratingCount = 0, onChange, maxStars = 5, readOnly = false }) {
  const [hoverValue, setHoverValue] = useState(0);

  const handleClick = (starIndex) => {
    if (readOnly) return;
    const newRating = starIndex === value ? 0 : starIndex;
    if (onChange) {
      onChange(newRating);
    }
  };

  const handleMouseEnter = (starIndex) => {
    if (readOnly) return;
    setHoverValue(starIndex);
  };

  const handleMouseLeave = () => {
    if (readOnly) return;
    setHoverValue(0);
  };

  // During hover, show the user's prospective selection
  // Otherwise, show the average rating
  const displayValue = hoverValue || averageRating;

  const getStarIcon = (starIndex) => {
    if (starIndex <= Math.floor(displayValue)) {
      return "star-filled";
    }
    if (starIndex === Math.ceil(displayValue) && displayValue % 1 >= 0.25) {
      return "star-half";
    }
    return "star";
  };

  return (
    <Box>
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <SpaceBetween direction="horizontal" size="xxxs">
          {Array.from({ length: maxStars }, (_, index) => {
            const starIndex = index + 1;

            return (
              <span
                key={starIndex}
                onClick={() => handleClick(starIndex)}
                onMouseEnter={() => handleMouseEnter(starIndex)}
                onMouseLeave={handleMouseLeave}
                role="button"
                aria-label={`Rate ${starIndex} out of ${maxStars} stars`}
                tabIndex={readOnly ? -1 : 0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleClick(starIndex);
                  }
                }}
                style={{
                  cursor: readOnly ? "default" : "pointer",
                  color: "#ff9900",
                  lineHeight: 1,
                }}
              >
                <Icon name={getStarIcon(starIndex)} />
              </span>
            );
          })}
        </SpaceBetween>
        {averageRating > 0 && (
          <span style={{ fontSize: "14px", fontWeight: 600, color: "#0f141a" }}>
            {averageRating.toFixed(1)} <span style={{ fontWeight: 400, color: "#5f6b7a" }}>({ratingCount} {ratingCount === 1 ? "rating" : "ratings"})</span>
          </span>
        )}
      </div>
      {value > 0 && (
        <div style={{ fontSize: "12px", color: "#0972d3", marginTop: "4px" }}>
          Your rating: {value} {value === 1 ? "star" : "stars"}
        </div>
      )}
    </Box>
  );
}

export default StarRating;
